import { State } from '@progress/kendo-data-query';
import {
  GridDataStateChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid';
import { useCallback, useMemo, useState } from 'react';

import { useSWR } from '~/hooks/useSWR';

import { getEmployeesData } from './data';
import { MyTeamEmployeesData } from './models';

export interface UseMyTeamOutput {
  employeesDataTable: EmployeesDataTable[];
  loading: boolean;
  failedToLoad: boolean;
  gridDataState: State;
  handleGridDataStateChange: (e: GridDataStateChangeEvent) => void;
  handleGridPageChange: (e: GridPageChangeEvent) => void;
}

export interface EmployeesDataTable extends MyTeamEmployeesData {
  currentPosition: string | undefined;
  currentSalary: number | undefined;
  currentCompensation: number | undefined;
  currentPerformance: number | undefined;
}

export const useMyTeam = (): UseMyTeamOutput => {
  const {
    loading,
    failedToLoad,
    data: employeesData,
  } = useSWR('/myTeamTable', getEmployeesData);

  const [gridDataState, setGridDataState] = useState({
    skip: 0,
    take: 4,
  } as State);

  const handleGridDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setGridDataState(e.dataState);
    },
    [setGridDataState],
  );
  const handleGridPageChange = useCallback(
    (e: GridPageChangeEvent) => {
      setGridDataState((currentState: State) => ({
        ...currentState,
        ...e.page,
      }));
    },
    [setGridDataState],
  );

  const employeesDataTable: EmployeesDataTable[] = useMemo(
    () =>
      !!employeesData
        ? employeesData.map((employeeData: MyTeamEmployeesData) => {
            const { salary, internalPosition, compensation, performanceScore } =
              employeeData;
            return {
              ...employeeData,
              currentPosition: internalPosition?.position,
              currentSalary: salary?.amount,
              currentCompensation: compensation?.amount,
              currentPerformance: performanceScore?.score,
            };
          })
        : [],
    [employeesData],
  );

  return {
    loading,
    failedToLoad,
    employeesDataTable,
    gridDataState,
    handleGridDataStateChange,
    handleGridPageChange,
  };
};
